import { Link } from "@/components/LocalizedLink";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Line, Reveal, Overline } from "@/components/Kinetic";
import FlowDiagram from "@/components/FlowDiagram";
import { useLang } from "@/i18n/LanguageContext";

const HERO_STEPS_EN = [
  { index: "01", title: "Product", desc: "What is being sold", chips: ["Industry", "Use case"] },
  { index: "02", title: "Channel", desc: "Who should sell it", chips: ["Distributor", "Importer"] },
  { index: "03", title: "Market", desc: "Where it can sell", chips: ["Region", "Demand signals"] },
  { index: "04", title: "Companies", desc: "Who may be relevant", chips: ["Discovery", "Relevance"] },
];

const HERO_STEPS_TR = [
  { index: "01", title: "Ürün", desc: "Ne satılıyor", chips: ["Sektör", "Kullanım alanı"] },
  { index: "02", title: "Kanal", desc: "Kim satmalı", chips: ["Distribütör", "İthalatçı"] },
  { index: "03", title: "Pazar", desc: "Nerede satılabilir", chips: ["Bölge", "Talep sinyalleri"] },
  { index: "04", title: "Şirketler", desc: "Kimler uygun olabilir", chips: ["Keşif", "Uygunluk"] },
];

export default function HeroSection() {
  const { lang, tx } = useLang();
  const steps = lang === "tr" ? HERO_STEPS_TR : HERO_STEPS_EN;

  return (
    <section className="relative overflow-hidden bg-paper pt-32 sm:pt-40 lg:pt-44">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(11,15,23,0.045) 1px, transparent 1px)",
          backgroundSize: "clamp(48px, 8vw, 120px) 100%",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-4 pb-20 sm:px-6 sm:pb-28 lg:px-8 lg:pb-32">
        <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-7">
            <Reveal immediate y={16}>
              <Overline>{tx("GADULABS — Software company", "GADULABS — Yazılım şirketi")}</Overline>
            </Reveal>
            <h1
              data-testid="hero-title"
              className="mt-7 font-display text-4xl font-bold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl"
            >
              {lang === "tr" ? (
                <>
                  <Line delay={0.1}>Karmaşık iş problemleri</Line>
                  <Line delay={0.22}>için yazılım.</Line>
                </>
              ) : (
                <>
                  <Line delay={0.1}>Software for complex</Line>
                  <Line delay={0.22}>business problems.</Line>
                </>
              )}
            </h1>
            <Reveal immediate delay={0.45} y={20} className="mt-7 max-w-xl">
              <p className="text-base leading-relaxed text-slate-600 sm:text-lg">
                {tx(
                  "GADULABS builds intelligent software products that turn difficult, fragmented business processes into focused digital tools.",
                  "GADULABS, zorlu ve parçalı iş süreçlerini odaklı dijital araçlara dönüştüren akıllı yazılım ürünleri geliştirir.",
                )}
              </p>
            </Reveal>
            <Reveal immediate delay={0.58} y={20} className="mt-8">
              <Link
                to="/products/cernio"
                data-testid="hero-cernio-intro-link"
                className="focus-ring group inline-flex max-w-full items-start gap-3 border border-slate-200 bg-white px-5 py-4 transition-colors duration-300 hover:border-navy/40"
              >
                <span className="mt-1.5 h-2 w-2 shrink-0 bg-cobalt" aria-hidden="true" />
                <span className="text-sm leading-relaxed text-slate-600">
                  <span className="font-display font-bold text-ink">
                    {tx("Meet Cernio", "Cernio ile tanışın")}
                  </span>{" "}
                  {tx(
                    "— our AI-powered market and channel intelligence platform for exporters.",
                    "— ihracatçılar için yapay zekâ destekli pazar ve kanal istihbaratı platformumuz.",
                  )}
                  <ArrowRight
                    size={14}
                    className="ml-2 inline text-slate-400 transition-transform duration-300 group-hover:translate-x-1"
                  />
                </span>
              </Link>
            </Reveal>
            <Reveal immediate delay={0.7} y={20} className="mt-9 flex flex-wrap items-center gap-4">
              <a
                href="https://getcernio.com"
                data-testid="hero-cta-button"
                className="focus-ring group inline-flex items-center gap-2 bg-ink px-6 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-navy"
              >
                {tx("Explore Cernio", "Cernio'yu Keşfedin")}
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </a>
              <Link
                to="/company"
                data-testid="hero-secondary-button"
                className="focus-ring inline-flex items-center gap-2 border border-slate-300 bg-transparent px-6 py-3.5 text-sm font-medium text-ink transition-colors duration-300 hover:border-ink"
              >
                {tx("About GADULABS", "GADULABS Hakkında")}
              </Link>
            </Reveal>
          </div>

          <Reveal immediate delay={0.5} y={32} className="lg:col-span-5">
            <FlowDiagram
              steps={steps}
              label={tx("System / Cernio flow", "Sistem / Cernio akışı")}
              testid="hero-flow"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
